import { Icon } from '@iconify/react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Tooltip,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { LoadingState } from '../../../components/ui';
import {
  getSpeciesById,
  addSpeciesGroup,
  renameSpeciesGroup,
  removeSpeciesGroup,
} from 'common/src/api/species';

interface Props {
  speciesId: number;
}

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

/**
 * Administración de los grupos de una especie.
 *
 * El campo Grupo del modal de SKU solo permite crear; acá se puede además
 * renombrar y eliminar, que es lo que hace falta para corregir un typo.
 *
 * Todo pasa por RPC y no por UPDATE directo: `species` solo la escribe un
 * superadmin, y renombrar o eliminar tiene que arrastrar los SKUs que
 * apuntaban al valor viejo para no dejarlos huérfanos.
 * Ver migración 20260803120000_species_group_rpcs.sql.
 */
const SpeciesConfigTab: React.FC<Props> = ({ speciesId }) => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ value: string; next: string } | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const species = await getSpeciesById(speciesId);
      setGroups(species?.groups ?? []);
    } catch (e: unknown) {
      toast.error(`Error cargando la especie: ${getErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  }, [speciesId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<string[]>, okMsg: string) => {
    setBusy(true);
    try {
      setGroups(await fn());
      toast.success(okMsg);
      return true;
    } catch (e: unknown) {
      toast.error(getErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!draft.trim()) return;
    if (await run(() => addSpeciesGroup(speciesId, draft), `"${draft.trim()}" agregado`)) {
      setDraft('');
    }
  };

  const handleRename = async () => {
    if (!editing || !editing.next.trim()) return;
    if (
      await run(
        () => renameSpeciesGroup(speciesId, editing.value, editing.next),
        `Renombrado a "${editing.next.trim()}"`
      )
    ) {
      setEditing(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <LoadingState label='Cargando...' />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className='space-y-3'>
        <div>
          <h3 className='font-semibold'>Grupos de la especie</h3>
          <p className='text-sm text-default-500'>
            Tags a criterio del equipo (ej: Reds, Blacks). Cada SKU se puede
            asignar a uno. Al renombrar o eliminar un grupo, los SKUs que lo
            usaban se actualizan solos.
          </p>
        </div>

        {editing ? (
          <div className='flex gap-2'>
            <Input
              size='sm'
              autoFocus
              value={editing.next}
              onChange={(e) => setEditing({ ...editing, next: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') setEditing(null);
              }}
            />
            <Button
              size='sm'
              color='primary'
              isLoading={busy}
              onPress={handleRename}
              startContent={!busy ? <Icon icon='tabler:check' /> : undefined}
            >
              Guardar
            </Button>
            <Button
              size='sm'
              variant='light'
              onPress={() => setEditing(null)}
              startContent={<Icon icon='tabler:x' />}
            >
              Cancelar
            </Button>
          </div>
        ) : (
          <div className='flex gap-2'>
            <Input
              size='sm'
              placeholder='Nuevo grupo'
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <Button
              size='sm'
              color='primary'
              isLoading={busy}
              onPress={handleAdd}
              isDisabled={!draft.trim()}
              startContent={!busy ? <Icon icon='tabler:plus' /> : undefined}
            >
              Agregar
            </Button>
          </div>
        )}

        {groups.length === 0 ? (
          <p className='text-sm text-default-400 italic'>
            Todavía no hay grupos. Agrega el primero para poder clasificar los
            SKUs.
          </p>
        ) : (
          <div className='flex flex-wrap gap-2'>
            {groups.map((group) => (
              <Chip
                key={group}
                variant='flat'
                className='h-8 pr-1'
                endContent={
                  <span className='flex items-center gap-1'>
                    <Tooltip content='Renombrar'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        aria-label={`Renombrar ${group}`}
                        onPress={() => setEditing({ value: group, next: group })}
                      >
                        <Icon icon='tabler:edit' className='text-xs' />
                      </Button>
                    </Tooltip>
                    <Tooltip content='Eliminar'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='danger'
                        aria-label={`Eliminar ${group}`}
                        onPress={() =>
                          run(
                            () => removeSpeciesGroup(speciesId, group),
                            `"${group}" eliminado`
                          )
                        }
                      >
                        <Icon icon='tabler:trash' className='text-xs' />
                      </Button>
                    </Tooltip>
                  </span>
                }
              >
                {group}
              </Chip>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SpeciesConfigTab;
